import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconCircleCheck, IconCircleX, IconClockHour4, IconLoader2, IconRefresh } from "@tabler/icons-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/ui/logo";
import { cn } from "@/lib/utils/cn";
import { getRegistrationStatus, type RegistrationStatus } from "../api";

/**
 * Acompanhamento do cadastro self-serve/convite (público, sem sessão). Recebe o
 * status inicial via location.state e consulta por e-mail até o backoffice liberar.
 * Aprovado → faz login; recusado → mostra o motivo.
 */
export function PendingStatusPage() {
  const location = useLocation();
  const initial = (location.state as RegistrationStatus | null) ?? null;
  const [registration, setRegistration] = useState<RegistrationStatus | null>(initial);
  const [checking, setChecking] = useState(false);

  const status = registration?.status ?? "pending";
  const rejected = status === "rejected";
  const approved = status === "approved";

  async function recheck() {
    if (!registration?.email) return;
    setChecking(true);
    try {
      const next = await getRegistrationStatus(registration.email);
      setRegistration(next);
      if (next.status === "approved") toast.success("Cadastro aprovado! Você já pode entrar.");
      else if (next.status === "pending") toast.info("Ainda em análise. Tente novamente em breve.");
    } catch {
      toast.error("Não foi possível verificar o status agora.");
    } finally {
      setChecking(false);
    }
  }

  const icon = approved ? IconCircleCheck : rejected ? IconCircleX : IconClockHour4;
  const tone = approved
    ? "bg-success-soft text-success"
    : rejected
      ? "bg-danger-soft text-danger"
      : "bg-warning-soft text-warning";
  const title = approved
    ? "Cadastro aprovado!"
    : rejected
      ? "Cadastro não aprovado"
      : "Cadastro em análise";
  const body = approved
    ? "Sua imobiliária foi liberada. Entre para acessar o portal."
    : rejected
      ? "Infelizmente seu cadastro não foi aprovado."
      : "Recebemos seu cadastro. Nossa equipe vai validar os dados e liberar o acesso.";

  const Icon = icon;

  return (
    <Card className="p-8 text-center shadow-card border-border/60">
      <div className="mb-5 flex flex-col items-center gap-4">
        <Logo />
        <span className={cn("flex h-14 w-14 items-center justify-center rounded-full", tone)} aria-hidden>
          <Icon size={30} />
        </span>
        <div>
          <h1 className="text-lg font-bold tracking-tight text-ink">{title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-muted">{body}</p>
        </div>
      </div>

      {rejected && registration?.rejectionReason && (
        <p className="mb-5 rounded-lg bg-danger-soft px-3 py-2 text-[12.5px] text-danger">
          {registration.rejectionReason}
        </p>
      )}

      {registration?.email && (
        <p className="mb-5 text-xs text-ink-soft">
          Conta: <span className="font-medium text-ink">{registration.email}</span>
        </p>
      )}

      <div className="flex flex-col gap-2">
        {!rejected && !approved && registration?.email && (
          <button
            type="button"
            onClick={recheck}
            disabled={checking}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-border text-sm font-medium transition-all hover:bg-gray-50 disabled:opacity-60"
          >
            {checking ? <IconLoader2 size={16} className="animate-spin" /> : <IconRefresh size={16} />}
            {checking ? "Verificando..." : "Verificar novamente"}
          </button>
        )}
        <Link
          to="/login"
          className={cn(
            "inline-flex h-11 w-full items-center justify-center rounded-xl text-sm font-bold transition-all",
            approved
              ? "bg-brand-grad text-white shadow-glow hover:scale-[1.01]"
              : "text-ink-muted hover:text-ink",
          )}
        >
          {approved ? "Entrar no Portal" : "Voltar para o login"}
        </Link>
      </div>
    </Card>
  );
}
